import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Keyed by data-filter-param-name. Survives section re-renders within a single page lifecycle.
if (!window.__tkAccordionState) window.__tkAccordionState = new Map();

// is re-rendered, which fires toggle events on details elements as their `open` attribute is
// post-morph state (collapsed) and subsequent renders propagate the collapse forever.
// We also restore scroll position drift caused by view transitions / layout reflow.
if (!window.__tkFilterMorphBooted) {
  window.__tkFilterMorphBooted = true;
  window.__tkMorphInFlight = 0;

  const reapplyAccordionState = () => {
    document.querySelectorAll('accordion-custom[data-filter-param-name]').forEach((el) => {
      const key = el.getAttribute('data-filter-param-name');
      if (!key) return;
      const stored = window.__tkAccordionState.get(key);
      if (typeof stored !== 'boolean') return;
      const details = el.querySelector('details');
      if (details && details.open !== stored) details.open = stored;
    });
  };

  // any filter accordion, regardless of whether the <details> element was replaced by morph
  // (per-instance listeners on details elements are lost when the element is replaced).
  // toggle does not bubble (per spec), but capture phase still propagates from document.
  document.addEventListener(
    'toggle',
    (e) => {
      if (!(e.target instanceof HTMLDetailsElement)) return;
      const accordion = e.target.closest('accordion-custom[data-filter-param-name]');
      if (!accordion) return;
      if ((window.__tkMorphInFlight || 0) > 0) return;
      const key = accordion.getAttribute('data-filter-param-name');
      if (key) window.__tkAccordionState.set(key, e.target.open);
    },
    true,
  );

  // Watch for `open` attribute changes on filter accordions during morph and re-apply
  // stored state immediately. Outside morph window, observer is a no-op.
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      if ((window.__tkMorphInFlight || 0) === 0) return;
      let touched = false;
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'open') {
          if (m.target.closest && m.target.closest('accordion-custom[data-filter-param-name]')) {
            touched = true;
            break;
          }
        } else if (m.type === 'childList') {
          touched = true;
          break;
        }
      }
      if (touched) reapplyAccordionState();
    }).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  /**
   * Custom smooth-scroll with a deliberate ease-out curve. Browser-default
   * `behavior: 'smooth'` ends up looking instant on short distances; this gives
   * a consistent ~700ms premium-feeling animation regardless of distance.
   * Respects prefers-reduced-motion (jumps instantly when user opted out).
   */
  const tkAnimateScroll = (targetY, duration = 700) => {
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 4) return;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, targetY);
      return;
    }

    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + distance * ease(progress));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  /**
   * Scroll to top of the product grid with generous breathing room above
   * (so user sees the title/sort/filter row before the products themselves).
   */
  const scrollToResultsTop = () => {
    const results = document.getElementById('ResultsList');
    if (!results) return;
    const headerVar = getComputedStyle(document.body).getPropertyValue('--header-height');
    const headerH = parseFloat(headerVar) || 60;
    const buffer = 120;
    const targetY = Math.max(0, results.getBoundingClientRect().top + window.scrollY - headerH - buffer);
    tkAnimateScroll(targetY, 700);
  };

  document.addEventListener('filter:update', () => {
    window.__tkMorphInFlight++;
    const grid = document.getElementById('ResultsList');
    if (grid) grid.setAttribute('data-tk-morphing', '');

    const release = () => {
      reapplyAccordionState();
      window.__tkMorphInFlight = Math.max(0, window.__tkMorphInFlight - 1);
      if (grid) grid.removeAttribute('data-tk-morphing');
    };
    setTimeout(release, 2000);
  });
}

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#restoreMoOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  #restoreMoOpenState() {
    const key = this.dataset.filterParamName;
    if (!key) return;
    const stored = window.__tkAccordionState.get(key);
    if (typeof stored === 'boolean') {
      this.details.open = stored;
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
